import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, CheckCircle, MessageCircle, Send, User, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { usePartner } from '../lib/partnerContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function RadarSuiviPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partner = usePartner(searchParams.toString());
  const demandeId = searchParams.get('demande');
  const emailParam = searchParams.get('email');
  
  const [email, setEmail] = useState(emailParam || '');
  const [loading, setLoading] = useState(false);
  const [demande, setDemande] = useState(null);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  const fetchDemande = async () => {
    if (!demandeId || !email) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/api/radar/suivi/${demandeId}?email=${encodeURIComponent(email)}`);
      setDemande(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Demande non trouvée ou accès non autorisé');
      setDemande(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailParam && demandeId) {
      fetchDemande();
    }
  }, [emailParam, demandeId]);

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${API}/api/radar/suivi/${demandeId}/message?email=${encodeURIComponent(email)}`,
        { content: newMessage }
      );
      toast.success('Message envoyé');
      setNewMessage('');
      fetchDemande();
    } catch (err) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSendingMessage(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">En attente</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-500/20 text-blue-300 border-blue-500/30">En cours</Badge>;
      case 'completed':
        return <Badge className="bg-green-500/20 text-green-300 border-green-500/30">Terminé</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-black/30 backdrop-blur-sm border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(partner.link('/radar'))}
              className="text-white/70 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Retour au Radar
            </Button>
            <h1 className="text-xl font-bold text-white">Suivi de ma demande</h1>
            {demande?.tracking_code && (
              <span className="ml-2 text-sm bg-purple-500/30 text-purple-200 px-3 py-1 rounded-full font-mono">{demande.tracking_code}</span>
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Email verification if not provided */}
        {!demande && !loading && (
          <Card className="bg-white/10 border-white/20">
            <CardHeader>
              <CardTitle className="text-white">Accéder à ma demande</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-white/70 mb-4">
                Entrez l'email utilisé lors de votre commande pour accéder au suivi.
              </p>
              <div className="space-y-4">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="votre@email.com"
                  className="bg-white/10 border-white/20 text-white"
                />
                <Button
                  onClick={fetchDemande}
                  disabled={loading || !email}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Accéder à ma demande
                </Button>
                {error && (
                  <div className="bg-red-500/20 text-red-300 p-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {error}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-400 mx-auto mb-4" />
            <p className="text-white/60">Chargement...</p>
          </div>
        )}

        {demande && (
          <div className="space-y-6">
            {/* Status Card */}
            <Card className="bg-white/10 border-white/20">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {demande.status === 'completed' ? (
                      <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      </div>
                    ) : (
                      <div className="w-12 h-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                        <Clock className="w-6 h-6 text-blue-400" />
                      </div>
                    )}
                    <div>
                      <h2 className="text-lg font-semibold text-white">
                        {demande.status === 'completed' ? 'Demande terminée' : 'Demande en cours'}
                      </h2>
                      <p className="text-white/60 text-sm">
                        Payée le {formatDate(demande.paid_at)}
                      </p>
                    </div>
                  </div>
                  {getStatusBadge(demande.status)}
                </div>

                {demande.status !== 'completed' && (
                  <div className="bg-blue-500/20 p-3 rounded-lg border border-blue-500/30">
                    <p className="text-sm text-white/80">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Délai estimé : 7 à 10 jours ouvrés
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Parcelle Info */}
            {demande.parcelle && (
              <Card className="bg-white/10 border-white/20">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-purple-400" />
                    Parcelle recherchée
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-white/60 text-sm">Commune</p>
                      <p className="font-medium">{demande.parcelle.commune}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Code postal</p>
                      <p className="font-medium">{demande.parcelle.code_postal}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Section</p>
                      <p className="font-medium">{demande.parcelle.section}</p>
                    </div>
                    <div>
                      <p className="text-white/60 text-sm">Numéro</p>
                      <p className="font-medium">{demande.parcelle.numero}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Proprietaire Result */}
            {demande.proprietaire && (
              <Card className="bg-green-500/10 border-green-500/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-green-400" />
                    Propriétaire identifié
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-white">
                  <div className="bg-green-500/20 p-4 rounded-lg">
                    <p className="text-xl font-semibold mb-2">
                      {demande.proprietaire.nom} {demande.proprietaire.prenom || ''}
                    </p>
                    {demande.proprietaire.adresse && (
                      <p className="text-white/80">
                        {demande.proprietaire.adresse}
                        {demande.proprietaire.code_postal || demande.proprietaire.ville ? (
                          <><br/>{demande.proprietaire.code_postal} {demande.proprietaire.ville}</>
                        ) : null}
                      </p>
                    )}
                  </div>
                  <p className="text-white/60 text-sm mt-3">
                    💡 Cette adresse est le domicile déclaré par le propriétaire.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Messages / Ticket */}
            <Card className="bg-white/10 border-white/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-purple-400" />
                  Discussion avec l'expert
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Messages List */}
                <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
                  {demande.messages && demande.messages.length > 0 ? (
                    demande.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg ${
                          msg.sender === 'admin'
                            ? 'bg-purple-500/20 border border-purple-500/30'
                            : 'bg-white/10 border border-white/20'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${
                            msg.sender === 'admin' ? 'text-purple-300' : 'text-white'
                          }`}>
                            {msg.sender === 'admin' ? '👑 Expert' : '👤 Vous'}
                          </span>
                          <span className="text-white/40 text-xs">
                            {formatDate(msg.timestamp)}
                          </span>
                        </div>
                        <p className="text-white/80 text-sm">{msg.content}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-white/50 text-center py-4">
                      Aucun message pour le moment
                    </p>
                  )}
                </div>

                {/* Send Message */}
                <div className="flex gap-2">
                  <Textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Écrivez votre message..."
                    className="bg-white/10 border-white/20 text-white resize-none"
                    rows={2}
                  />
                  <Button
                    onClick={handleSendMessage}
                    disabled={sendingMessage || !newMessage.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {sendingMessage ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
}
